import { LIMITS } from '../constants/limits';

export interface ValidationResult<T> {
  ok: true;
  value: T;
}
export interface ValidationFailure {
  ok: false;
  message: string;
}
export type Validated<T> = ValidationResult<T> | ValidationFailure;

/** 한글(완성형, 자모 포함), 영문, 숫자, 일반 공백만 허용 */
const NICKNAME_PATTERN = /^[0-9A-Za-z가-힣ㄱ-ㅎㅏ-ㅣ ]+$/;

export function validateNickname(raw: unknown): Validated<string> {
  if (typeof raw !== 'string') return { ok: false, message: '닉네임을 입력해주세요.' };
  const value = raw.trim().replace(/\s+/g, ' ');
  if (value.length === 0) return { ok: false, message: '닉네임을 입력해주세요.' };
  if (value.length < LIMITS.NICKNAME_MIN || value.length > LIMITS.NICKNAME_MAX) {
    return { ok: false, message: `닉네임은 ${LIMITS.NICKNAME_MIN}~${LIMITS.NICKNAME_MAX}자로 입력해주세요.` };
  }
  if (!NICKNAME_PATTERN.test(value)) {
    return { ok: false, message: '닉네임에는 한글, 영문, 숫자, 공백만 사용할 수 있어요.' };
  }
  return { ok: true, value };
}

export function validateRoomName(raw: unknown): Validated<string> {
  if (raw === undefined || raw === null) return { ok: true, value: LIMITS.DEFAULT_ROOM_NAME };
  if (typeof raw !== 'string') return { ok: false, message: '방 이름 형식이 올바르지 않아요.' };
  // 제어 문자 제거 + 공백 정리
  const value = raw.replace(/[\u0000-\u001f\u007f<>]/g, '').trim().replace(/\s+/g, ' ');
  if (value.length === 0) return { ok: true, value: LIMITS.DEFAULT_ROOM_NAME };
  if (value.length > LIMITS.ROOM_NAME_MAX) {
    return { ok: false, message: `방 이름은 최대 ${LIMITS.ROOM_NAME_MAX}자까지 가능해요.` };
  }
  return { ok: true, value };
}

export function validateMaxParticipants(raw: unknown): Validated<number> {
  const n = typeof raw === 'string' ? Number(raw) : raw;
  if (typeof n !== 'number' || !Number.isInteger(n)) {
    return { ok: false, message: '참여 인원을 올바르게 입력해주세요.' };
  }
  if (n < LIMITS.PARTICIPANTS_MIN || n > LIMITS.PARTICIPANTS_MAX) {
    return { ok: false, message: `참여 인원은 ${LIMITS.PARTICIPANTS_MIN}~${LIMITS.PARTICIPANTS_MAX}명 사이여야 해요.` };
  }
  return { ok: true, value: n };
}

/** 비밀번호: 미설정(undefined/빈 문자열)이면 null */
export function validatePassword(raw: unknown): Validated<string | null> {
  if (raw === undefined || raw === null || raw === '') return { ok: true, value: null };
  if (typeof raw !== 'string') return { ok: false, message: '비밀번호 형식이 올바르지 않아요.' };
  if (raw.length < LIMITS.PASSWORD_MIN || raw.length > LIMITS.PASSWORD_MAX) {
    return { ok: false, message: `비밀번호는 ${LIMITS.PASSWORD_MIN}~${LIMITS.PASSWORD_MAX}자로 입력해주세요.` };
  }
  return { ok: true, value: raw };
}

const ROOM_ID_PATTERN = new RegExp(`^[0-9A-Za-z]{${LIMITS.ROOM_ID_LENGTH}}$`);
export function isValidRoomId(id: unknown): id is string {
  return typeof id === 'string' && ROOM_ID_PATTERN.test(id);
}
